import { useRef } from 'react';
import {
  ScrollView,
  Platform,
  LayoutChangeEvent,
  Dimensions,
  NativeScrollEvent,
  NativeSyntheticEvent,
  UIManager,
  findNodeHandle,
} from 'react-native';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { LayoutObject } from '@noriginmedia/react-spatial-navigation';

import { easeOutCubic } from '../utils/functions';

const { height: SCREEN_HEIGHT } = Dimensions.get('screen');

export const useScrollPostion = <T extends ScrollView>() => {
  const scrollRef = useRef<T>(null);
  const scrollPosition = useRef(0);
  const visibleHeight = useRef(SCREEN_HEIGHT);

  const onLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    visibleHeight.current = nativeEvent.layout.height;
  };

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollPosition.current = event.nativeEvent.contentOffset.y;
  };

  const scrollToTop = (element: LayoutObject) => {
    if (Platform.OS === 'web') {
      handleElementFocus(element);
    } else {
      scrollRef.current?.scrollTo({
        animated: true,
        x: 0,
        y: 0,
      });
    }
  };

  const handleElementFocus = (element: LayoutObject) => {
    if (Platform.OS !== 'web') {
      const elementNode = findNodeHandle(element.node as any);
      const scrollNode = findNodeHandle(scrollRef.current);

      if (elementNode === null || scrollNode === null) {
        return;
      }

      UIManager.measureLayout(
        elementNode,
        scrollNode,
        () => {},
        (_left, top, _width, height) => {
          const offsetY = visibleHeight.current / 2 - height / 2;

          scrollRef.current?.scrollTo({
            animated: true,
            x: 0,
            y: top - offsetY,
          });
        }
      );

      return;
    }

    scrollIntoView(element.node, {
      ease: easeOutCubic,
    });
  };

  return {
    scrollRef,
    scrollToTop,
    handleElementFocus,
    scrollProps: {
      onLayout,
      onScroll,
      showsVerticalScrollIndicator: false,
      scrollEventThrottle: 16,
    },
  };
};
